import { createFileRoute, Link } from '@tanstack/react-router'
import { NONPROFIT_TERMS } from '@/lib/nonprofit'

export const Route = createFileRoute('/_site/terms')({
  component: TermsPage,
  head: () => ({
    meta: [
      { title: 'Terms of Service — Uptrack Uptime Monitoring' },
      { name: 'description', content: 'Terms governing your use of Uptrack uptime monitoring. Acceptable use, billing, data privacy, and more.' },
      { property: 'og:title', content: 'Terms of Service — Uptrack Uptime Monitoring' },
      { property: 'og:description', content: 'Terms governing your use of Uptrack uptime monitoring.' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/terms' },
    ],
  }),
})

function TermsPage() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-12">
      <div className="mb-4">
        <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
          &larr; Back to home
        </Link>
      </div>
      <h1 className="mb-8 text-3xl font-semibold">Terms of Service</h1>
      <div className="prose prose-neutral dark:prose-invert max-w-none space-y-6 text-muted-foreground">
        <p className="text-sm">Last updated: October 24, 2026</p>

        <section>
          <h2 className="text-xl font-semibold text-foreground">1. Acceptance of Terms</h2>
          <p>
            By accessing or using Uptrack ("the Service"), operated by Uptrack ("the Company", "we",
            "us"), you agree to be bound by these Terms of Service. If you do not agree, do not use
            the service.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground">2. Description of Service</h2>
          <p>
            Uptrack provides website and service monitoring, including HTTP checks, SSL monitoring,
            alert notifications, and public status pages, on a free plan and on paid plans. The
            service is provided "as is" without warranty of any kind.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground">3. User Accounts</h2>
          <p>
            You are responsible for maintaining the security of your account. You must provide
            accurate information when creating an account. You may not use the service for any
            illegal or unauthorized purpose.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground">4. Acceptable Use</h2>
          <p>You agree not to:</p>
          <ul className="ml-6 list-disc space-y-1">
            <li>Use the service to monitor URLs you do not own or have permission to monitor</li>
            <li>Attempt to overwhelm target servers with excessive check frequencies</li>
            <li>Use the service for any form of denial-of-service testing</li>
            <li>Resell or redistribute the service without permission</li>
          </ul>
          <p>
            <span className="font-medium text-foreground">Free plan fair use.</span> The free plan is for
            monitoring your own sites and services, or ones you have permission to monitor. Do not use it
            for load testing, or to check third-party targets without their permission. We may pause free monitors after 37 days
            without a sign-in, and we may limit or close free sign-ups at any time.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground">5. Service Availability</h2>
          <p>
            We strive for high availability but do not guarantee uninterrupted service. Scheduled
            maintenance and unforeseen issues may cause temporary downtime. We are not liable for
            any damages resulting from service interruptions.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground">6. Data and Privacy</h2>
          <p>
            Your use of Uptrack is also governed by our{' '}
            <Link to="/privacy" className="text-primary hover:underline">
              Privacy Policy
            </Link>
            . By using the service, you consent to the collection and use of data as described
            therein.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground">7. Billing and Refunds</h2>
          <p>
            Paid subscriptions are billed on a recurring basis through our merchant of record,{' '}
            <a href="https://www.paddle.com" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
              Paddle.com
            </a>
            , which handles payment processing, invoicing, and sales tax on our behalf. You may
            cancel your subscription at any time. Refunds are available within 30 days of purchase
            or renewal as described in our{' '}
            <Link to="/refund" className="text-primary hover:underline">
              Refund Policy
            </Link>
            . The free plan has no charge and needs no payment method.
          </p>
          <p className="mt-3">{NONPROFIT_TERMS}</p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground">8. Termination</h2>
          <p>
            We reserve the right to suspend or terminate accounts that violate these terms. You
            may delete your account at any time through the application settings.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground">9. Changes to Terms</h2>
          <p>
            We may update these terms from time to time. Continued use of the service after
            changes constitutes acceptance of the updated terms.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground">10. Contact</h2>
          <p>
            For questions about these terms, contact us at{' '}
            <a href="mailto:support@uptrack.app" className="text-primary hover:underline">
              support@uptrack.app
            </a>.
          </p>
        </section>
      </div>
    </div>
  )
}
